import React, { useState, useEffect, memo } from 'react';

import * as dateFns from 'date-fns';
import {
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  Stepper,
  Step,
  StepLabel,
  Typography,
} from '@mui/material';
import {
  BadgeRounded,
  AccountBalanceRounded,
  SavingsRounded,
  WavingHandRounded,
} from '@mui/icons-material';
import { useAuthContext } from 'src/auth/hooks';
import { useSearchParams } from 'src/routes/hook';
import _ from 'lodash';
import Iconify from '../iconify';
import {
  ProfileContent,
  FinancesContent,
  RetirementContent,
  ConnectContent,
  WelcomeContent,
} from './steps';
import { useOnboardingService } from './useOnboardingService';
import { useFinanceContext } from '../plaid/datasource';

export function useRequiresOnboarding() {
  const { user } = useAuthContext();
  const [requiresOnboarding, setRequiresOnboarding] = useState(false);
  const searchParams = useSearchParams();
  const { userId, accounts } = useFinanceContext();
  const onboarding = searchParams?.get('onboarding');

  useEffect(() => {
    if (userId && !user?.onboarded) {
      setRequiresOnboarding(_.isEmpty(accounts) || !user?.onboarded);
    }
  }, [userId, user, accounts]);

  return onboarding !== 'false' && (requiresOnboarding || onboarding);
}

function OnboardingDialog({ open, onClose }) {
  const { user } = useAuthContext();
  const service = useOnboardingService(user?.id);

  // TODO: Persist & restore partial onboarding
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({});

  const initializeFormData = () => {
    setFormData({
      profile: {
        firstName: user?.displayName?.split(' ')[0] ?? '',
        lastName: user?.displayName?.split(' ')[1] ?? '',
        addressLine1: user?.addressLine1 ?? '',
        addressLine2: user?.addressLine2 ?? '',
        city: user?.city ?? '',
        state: user?.state ?? null,
        zip: user?.zip ?? '',
        email: user?.email ?? '',
        dob: user?.dob ? dateFns.format(user?.dob?.toDate(), 'yyyy-MM-dd') : '',
      },
    });
  };

  useEffect(() => {
    initializeFormData();
    console.log(`formData: ${JSON.stringify(formData)}`);
  }, [user?.id]);

  const handleBack = () => {
    if (activeStep - 1 < 0) onClose();
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleNext = (data) => {
    setFormData((prev) => ({ ...prev, ...data }));

    /* if (activeStep === 1) {
      console.log('presave account...');
      service.preSaveProfile({ ...formData, ...data });
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else */
    if (activeStep === steps.length - 1) {
      handleSubmit({ ...formData, ...data });
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  // Handle completion of onboarding
  const handleSubmit = async (data) => {
    try {
      await service.save(data);
      onClose({ complete: true });
    } catch (error) {
      console.error('Failed to save onboarding data', error);
    }
  };

  const steps = [
    {
      name: 'Welcome',
      icon: <WavingHandRounded />,
      content: <WelcomeContent {...{ formData, onNext: handleNext, onBack: handleBack }} />,
    },
    {
      name: 'Accounts',
      icon: <AccountBalanceRounded />,
      content: <ConnectContent {...{ formData, onNext: handleNext, onBack: handleBack }} />,
    },
    {
      name: 'Profile',
      icon: <BadgeRounded />,
      content: <ProfileContent {...{ formData, onNext: handleNext, onBack: handleBack }} />,
    },
    // {
    //   name: 'Assumptions',
    //   icon: <SquareFootRounded />,
    //   content: <AssumptionsContent {...{ formData, onNext: handleNext, onBack: handleBack }} />,
    // },
    {
      name: 'Other Assets',
      icon: <SavingsRounded />,
      content: <FinancesContent {...{ formData, onNext: handleNext, onBack: handleBack }} />,
    },
    {
      name: 'Retirement',
      icon: <Iconify icon="icon-park-solid:vacation" width={24} />,
      content: <RetirementContent {...{ formData, onNext: handleNext, onBack: handleBack }} />,
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} fullScreen disableEscapeKeyDown>
      <DialogTitle>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ width: '100%' }}>
          {steps.map((step, index) => (
            <Step key={index}>
              <StepLabel>
                <div>
                  {step.icon} {/* Render icon */}
                  <Typography>{step.name}</Typography> {/* Render name */}
                </div>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>{activeStep < steps.length && steps[activeStep]?.content}</Stack>
      </DialogContent>
    </Dialog>
  );
}

export default memo(OnboardingDialog);
